<template>
  <div class="camera-container">
    <div class="video-wrapper">
      <video ref="video" autoplay playsinline class="video"></video>

      <!-- Guía vinculada al tamaño real del video (px) -->
      <div
        class="guide-box"
        :style="{
          width: guideW + 'px',
          height: guideH + 'px',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }"
      ></div>

      <div
        v-if="box.visible"
        class="detected-box"
        :class="box.status"
        :style="{
          left: (box.x * cssScale) + 'px',
          top: (box.y * cssScale) + 'px',
          width: (box.width * cssScale) + 'px',
          height: (box.height * cssScale) + 'px',
          transform: box.rotate ? 'rotate(' + box.rotate + 'deg)' : 'none',
          transformOrigin: 'left top'
        }"
      ></div>
    </div>

    <canvas ref="canvas" class="hidden"></canvas>

    <div class="btn-group">
      <button @click="startCamera" class="btn" :disabled="streamActive">Iniciar cámara</button>
      <button @click="retry" class="btn" v-if="capturedImage">Volver a tomar</button>
      <button @click="stopCamera" class="btn" v-if="streamActive">Detener cámara</button>
    </div>

    <div v-if="capturedImage" class="ocr-block">
      <div class="ocr-row">
        <div class="ocr-image-wrapper">
          <img :src="capturedImage" class="result" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: 'CameraIne',
  data() {
    return {
      videoEl: null,
      canvasEl: null,
      capturedImage: null,

      stream: null,
      processing: false,
      stableFrames: 0,
      focusStableFrames: 0,
      lastTime: 0,
      rafId: null,
      imageCapture: null,

      box: {
        visible: false,
        x: 0, y: 0, width: 0, height: 0,
        rotate: 0,
        status: 'bad'
      },

      streamActive: false,

      // Parámetros (ajustables)
      AREA_MIN: 50000,
      AREA_GOOD: 100000,
      RATIO_MIN: 1.3,
      RATIO_MAX: 1.85,
      STABLE_REQUIRED: 25,
      FOCUS_THRESHOLD: 150,
      FOCUS_STABLE_REQUIRED: 25,
      EMA_ALPHA: 0.2,

      // Buffer y calibración foco
      FOCUS_HISTORY_LEN: 8,
      focusHistory: [],
      focusCalibrated: false,
      focusSamples: [],
      FOCUS_CALIBRATE_FRAMES: 20,

      // Suavizado con límite
      MAX_DELTA_PER_FRAME: 60,
      bestPoly: null,

      // escala CSS del video
      cssScale: 1,

      // Guía vinculada al tamaño real del video
      GUIDE_RATIO: 1.58,
      GUIDE_SCALE: 0.90,
      guideW: 0,
      guideH: 0
    }
  },

  mounted() {
    this.videoEl = this.$refs.video
    this.canvasEl = this.$refs.canvas

    window.addEventListener('resize', this.updateScale)
    const updateAll = () => { this.updateScale(); this.setGuideBoxSize() }
    if (this.videoEl) {
      this.videoEl.addEventListener('loadedmetadata', updateAll)
      this.videoEl.addEventListener('playing', updateAll)
    }
  },

  beforeDestroy() {
    this.processing = false
    if (this.rafId) cancelAnimationFrame(this.rafId)
    if (this.stream) this.stream.getTracks().forEach(t => t.stop())
    window.removeEventListener('resize', this.updateScale)
  },

  methods: {
    // --- Utilidades UI/escala ---
    updateScale() {
      if (!this.videoEl) return
      const rect = this.videoEl.getBoundingClientRect()
      if (this.videoEl.videoWidth) {
        this.cssScale = rect.width / this.videoEl.videoWidth
      }
      this.setGuideBoxSize()
    },

    setGuideBoxSize() {
      if (!this.videoEl || !this.videoEl.videoWidth || !this.cssScale) return
      const targetW = Math.round(this.videoEl.videoWidth * this.GUIDE_SCALE)
      const targetH = Math.round(targetW / this.GUIDE_RATIO)
      this.guideW = Math.round(targetW * this.cssScale)
      this.guideH = Math.round(targetH * this.cssScale)
    },

    // --- Helpers generales ---
    isCvReadySafely() {
      return typeof window !== 'undefined' && typeof window.cv !== 'undefined'
    },

    isCentered(rect, width, height) {
      const centerX = rect.x + rect.width / 2
      const centerY = rect.y + rect.height / 2
      const margin = Math.min(width, height) * 0.18
      return (
        centerX > width / 2 - margin &&
        centerX < width / 2 + margin &&
        centerY > height / 2 - margin &&
        centerY < height / 2 + margin
      )
    },

    clamp(v, min, max) {
      return Math.max(min, Math.min(max, v))
    },

    smoothValue(oldV, targetV, alpha, maxDelta) {
      const ema = oldV + (targetV - oldV) * alpha
      const delta = ema - oldV
      if (Math.abs(delta) > maxDelta) {
        return oldV + Math.sign(delta) * maxDelta
      }
      return ema
    },

    smoothBox(target) {
      if (!this.box.visible) {
        this.box = { ...target }
        return
      }
      this.box.x = this.smoothValue(this.box.x, target.x, this.EMA_ALPHA, this.MAX_DELTA_PER_FRAME)
      this.box.y = this.smoothValue(this.box.y, target.y, this.EMA_ALPHA, this.MAX_DELTA_PER_FRAME)
      this.box.width = this.smoothValue(this.box.width, target.width, this.EMA_ALPHA, this.MAX_DELTA_PER_FRAME)
      this.box.height = this.smoothValue(this.box.height, target.height, this.EMA_ALPHA, this.MAX_DELTA_PER_FRAME)
      this.box.rotate = target.rotate || 0
      this.box.status = target.status
      this.box.visible = true
    },

    orderQuadPoints(pts) {
      const sum = pts.map(p => p.x + p.y)
      const diff = pts.map(p => p.x - p.y)
      const tl = pts[sum.indexOf(Math.min(...sum))]
      const br = pts[sum.indexOf(Math.max(...sum))]
      const tr = pts[diff.indexOf(Math.max(...diff))]
      const bl = pts[diff.indexOf(Math.min(...diff))]
      return [tl, tr, br, bl]
    },

    safeRoi(matGray, rect) {
      const x = Math.max(0, Math.min(rect.x, matGray.cols - 1))
      const y = Math.max(0, Math.min(rect.y, matGray.rows - 1))
      const w = Math.max(1, Math.min(rect.width, matGray.cols - x))
      const h = Math.max(1, Math.min(rect.height, matGray.rows - y))
      return matGray.roi(new cv.Rect(x, y, w, h))
    },

    laplacianVariance(matGray) {
      const lap = new cv.Mat()
      cv.Laplacian(matGray, lap, cv.CV_64F)
      const mean = new cv.Mat()
      const stddev = new cv.Mat()
      cv.meanStdDev(lap, mean, stddev)
      const variance = Math.pow(stddev.doubleAt(0, 0), 2)
      lap.delete(); mean.delete(); stddev.delete()
      return variance
    },

    capturePhoto(rect, polyPoints = null) {
      const v = this.videoEl
      const c = this.canvasEl
      c.width = v.videoWidth
      c.height = v.videoHeight
      const ctx = c.getContext('2d')
      ctx.drawImage(v, 0, 0, c.width, c.height)

      if (polyPoints && polyPoints.length === 4 && this.isCvReadySafely()) {
        const srcMat = cv.imread(c)
        const srcTri = cv.matFromArray(4, 1, cv.CV_32FC2, [
          polyPoints[0].x, polyPoints[0].y,
          polyPoints[1].x, polyPoints[1].y,
          polyPoints[2].x, polyPoints[2].y,
          polyPoints[3].x, polyPoints[3].y
        ])
        const widthA = Math.hypot(polyPoints[2].x - polyPoints[3].x, polyPoints[2].y - polyPoints[3].y)
        const widthB = Math.hypot(polyPoints[1].x - polyPoints[0].x, polyPoints[1].y - polyPoints[0].y)
        const maxW = Math.max(widthA, widthB)
        const heightA = Math.hypot(polyPoints[1].x - polyPoints[2].x, polyPoints[1].y - polyPoints[2].y)
        const heightB = Math.hypot(polyPoints[0].x - polyPoints[3].x, polyPoints[0].y - polyPoints[3].y)
        const maxH = Math.max(heightA, heightB)

        const dstTri = cv.matFromArray(4, 1, cv.CV_32FC2, [
          0, 0,
          maxW - 1, 0,
          maxW - 1, maxH - 1,
          0, maxH - 1
        ])

        const M = cv.getPerspectiveTransform(srcTri, dstTri)
        const dst = new cv.Mat()
        const dsize = new cv.Size(Math.round(maxW), Math.round(maxH))
        cv.warpPerspective(srcMat, dst, M, dsize, cv.INTER_LINEAR, cv.BORDER_CONSTANT, new cv.Scalar())

        const targetH = 1000
        let finalMat = dst
        if (dst.rows !== targetH) {
          const scaled = new cv.Mat()
          const scale = targetH / dst.rows
          cv.resize(dst, scaled, new cv.Size(Math.round(dst.cols * scale), targetH), 0, 0, cv.INTER_LINEAR)
          finalMat = scaled
          dst.delete()
        }

        const outCanvas = document.createElement('canvas')
        outCanvas.width = finalMat.cols
        outCanvas.height = finalMat.rows
        cv.imshow(outCanvas, finalMat)
        this.capturedImage = outCanvas.toDataURL('image/jpeg', 0.92)

        srcMat.delete(); srcTri.delete(); dstTri.delete(); M.delete()
        finalMat.delete()
      } else {
        const cropCanvas = document.createElement('canvas')
        cropCanvas.width = rect.width
        cropCanvas.height = rect.height
        const cropCtx = cropCanvas.getContext('2d')
        cropCtx.drawImage(
          c,
          rect.x, rect.y, rect.width, rect.height,
          0, 0, rect.width, rect.height
        )
        const targetH = 1000
        const scale = targetH / cropCanvas.height
        const scaledCanvas = document.createElement('canvas')
        scaledCanvas.width = Math.round(cropCanvas.width * scale)
        scaledCanvas.height = targetH
        scaledCanvas.getContext('2d').drawImage(cropCanvas, 0, 0, scaledCanvas.width, scaledCanvas.height)
        this.capturedImage = scaledCanvas.toDataURL('image/jpeg', 0.92)
      }
    },

    async processForOCRDataUrl(dataUrl) {
      if (!this.isCvReadySafely()) return dataUrl

      const img = await new Promise((res, rej) => {
        const i = new Image()
        i.onload = () => res(i)
        i.onerror = rej
        i.src = dataUrl
      })

      const tmp = document.createElement('canvas')
      tmp.width = img.width
      tmp.height = img.height
      const ctx = tmp.getContext('2d')
      ctx.drawImage(img, 0, 0)

      let src = cv.imread(tmp)
      let gray = new cv.Mat()
      cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY)

      let den = new cv.Mat()
      cv.bilateralFilter(gray, den, 9, 75, 75, cv.BORDER_DEFAULT)

      let clahe = new cv.Mat()
      let claheObj = new cv.CLAHE(2.0, new cv.Size(8, 8))
      claheObj.apply(den, clahe)
      claheObj.delete()

      let kernel = cv.matFromArray(3, 3, cv.CV_32F, [
        -1, -1, -1,
        -1, 9, -1,
        -1, -1, -1
      ])
      let sharp = new cv.Mat()
      cv.filter2D(clahe, sharp, cv.CV_8U, kernel)
      kernel.delete()

      let morphKernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(15, 15))
      let bg = new cv.Mat()
      cv.morphologyEx(sharp, bg, cv.MORPH_OPEN, morphKernel)
      let flatten = new cv.Mat()
      cv.subtract(sharp, bg, flatten)
      morphKernel.delete(); bg.delete()

      let bin = new cv.Mat()
      cv.adaptiveThreshold(flatten, bin, 255, cv.ADAPTIVE_THRESH_GAUSSIAN_C, cv.THRESH_BINARY, 21, 8)

      let k3 = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(3, 3))
      cv.morphologyEx(bin, bin, cv.MORPH_OPEN, k3)
      let k1x5 = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(5, 1))
      cv.morphologyEx(bin, bin, cv.MORPH_CLOSE, k1x5)
      k3.delete(); k1x5.delete()

      const outCanvas = document.createElement('canvas')
      cv.imshow(outCanvas, bin)

      src.delete(); gray.delete(); den.delete(); clahe.delete(); sharp.delete(); flatten.delete(); bin.delete()

      return outCanvas.toDataURL('image/png')
    },

    async startCamera() {
      if (this.streamActive) return
      if (this.stream) this.stopCamera()

      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        })
      } catch (e) {
        console.error('No se pudo acceder a la cámara', e)
        return
      }

      this.videoEl.srcObject = this.stream
      this.streamActive = true
      this.updateScale()

      try {
        const track = this.stream.getVideoTracks()[0]
        if ('ImageCapture' in window && track) this.imageCapture = new ImageCapture(track)
      } catch (e) {
        this.imageCapture = null
      }

      await new Promise(resolve => {
        let resolved = false
        const tryResolve = () => {
          if (resolved) return
          const hasVideoSize = this.videoEl && this.videoEl.videoWidth && this.videoEl.videoHeight
          const cvDefined = typeof window.cv !== 'undefined'
          const cvReady = cvDefined && (cv.ready || typeof cv.onRuntimeInitialized !== 'function' || cv.getBuildInformation)
          if (hasVideoSize && cvReady) {
            resolved = true
            resolve()
          }
        }
        if (this.videoEl) {
          this.videoEl.addEventListener('loadedmetadata', tryResolve, { once: true })
          this.videoEl.addEventListener('playing', tryResolve, { once: true })
        }
        if (window.cv && typeof cv.onRuntimeInitialized === 'function') {
          const prev = cv.onRuntimeInitialized
          cv.onRuntimeInitialized = () => {
            try { if (typeof prev === 'function') prev() } catch (e) {}
            tryResolve()
          }
        }
        const poll = setInterval(() => { tryResolve(); if (resolved) clearInterval(poll) }, 200)
        setTimeout(() => { if (!resolved) { resolved = true; clearInterval(poll); resolve() } }, 8000)
      })

      // Inicio del procesamiento
      this.processing = true
      this.stableFrames = 0
      this.focusStableFrames = 0
      this.focusHistory = []
      this.focusCalibrated = false
      this.focusSamples = []
      this.rafId = requestAnimationFrame(this.processFrame)
    },

    stopCamera() {
      this.processing = false
      this.streamActive = false
      if (this.rafId) { cancelAnimationFrame(this.rafId); this.rafId = null }
      if (this.imageCapture) this.imageCapture = null
      if (this.stream) {
        this.stream.getTracks().forEach(t => t.stop())
        this.stream = null
      }
    },

    retry() {
      this.capturedImage = null
      this.bestPoly = null
      this.box.visible = false
      this.stableFrames = 0
      this.focusStableFrames = 0
      this.focusHistory = []
      this.focusCalibrated = false

      if (!this.processing && this.stream) {
        this.processing = true
        this.rafId = requestAnimationFrame(this.processFrame)
      } else if (!this.stream) {
        this.startCamera()
      }
    },

    processFrame(time) {
      if (!this.processing) return
      this.lastTime = time || Date.now()

      const v = this.videoEl
      const c = this.canvasEl

      if (!v || !v.videoWidth || !v.videoHeight) {
        this.rafId = requestAnimationFrame(this.processFrame)
        return
      }

      c.width = v.videoWidth
      c.height = v.videoHeight
      const ctx = c.getContext('2d')
      ctx.drawImage(v, 0, 0, c.width, c.height)

      if (!this.isCvReadySafely()) {
        this.rafId = requestAnimationFrame(this.processFrame)
        return
      }

      let src = null
      let gray = null
      let blurred = null
      let edges = null
      let contours = null
      let hierarchy = null

      try {
        src = cv.imread(c)
        gray = new cv.Mat()
        blurred = new cv.Mat()
        edges = new cv.Mat()
        contours = new cv.MatVector()
        hierarchy = new cv.Mat()

        cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY)
        cv.GaussianBlur(gray, blurred, new cv.Size(5, 5), 0)
        cv.Canny(blurred, edges, 50, 150)

        cv.findContours(edges, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE)

        let bestRect = null
        let bestArea = 0
        let bestApproxLocal = null

        for (let i = 0; i < contours.size(); i++) {
          let cnt = contours.get(i)
          let area = cv.contourArea(cnt)
          if (area < this.AREA_MIN) { cnt.delete(); continue }

          const peri = cv.arcLength(cnt, true)
          const approx = new cv.Mat()
          cv.approxPolyDP(cnt, approx, 0.02 * peri, true)

          if (approx.rows === 4) {
            const rect = cv.boundingRect(approx)
            const areaRect = rect.width * rect.height
            if (areaRect > bestArea) {
              bestArea = areaRect
              bestRect = rect
              const pts = []
              for (let p = 0; p < 4; p++) {
                pts.push({ x: approx.intAt(p, 0), y: approx.intAt(p, 1) })
              }
              bestApproxLocal = pts
            }
          } else {
            const rect = cv.boundingRect(cnt)
            const ratio = rect.width / (rect.height || 1)
            const areaRect = rect.width * rect.height
            if (areaRect > 30000 && ratio > 1.2 && ratio < 1.95 && areaRect > bestArea) {
              bestArea = areaRect
              bestRect = rect
              bestApproxLocal = null
            }
          }

          approx.delete()
          cnt.delete()
        }

        // --- Parte 3: evaluación y disparo ---
        if (bestRect) {
          const ratio = bestRect.width / bestRect.height
          const area = bestRect.width * bestRect.height

          let status = 'bad'
          const goodRatio = ratio > this.RATIO_MIN && ratio < this.RATIO_MAX
          const bigEnough = area > this.AREA_GOOD
          const centered = this.isCentered(bestRect, c.width, c.height)

          if (goodRatio && bigEnough && centered) status = 'good'
          else if (goodRatio && bigEnough) status = 'medium'
          else status = 'bad'

          const targetBox = {
            visible: true,
            x: bestRect.x,
            y: bestRect.y,
            width: bestRect.width,
            height: bestRect.height,
            rotate: 0,
            status
          }
          this.smoothBox(targetBox)

          if (bestApproxLocal && bestApproxLocal.length === 4) {
            this.bestPoly = this.orderQuadPoints(bestApproxLocal)
          } else {
            this.bestPoly = null
          }

          const roi = this.safeRoi(gray, bestRect)
          const focusVar = this.laplacianVariance(roi)
          roi.delete()

          if (!this.focusCalibrated) {
            this.focusSamples.push(focusVar)
            if (this.focusSamples.length >= this.FOCUS_CALIBRATE_FRAMES) {
              const avg = this.focusSamples.reduce((a,b)=>a+b,0) / this.focusSamples.length
              this.FOCUS_THRESHOLD = Math.max(60, Math.round(avg * 0.65))
              this.focusCalibrated = true
              this.focusSamples = []
            }
          }

          this.focusHistory.push(focusVar)
          if (this.focusHistory.length > this.FOCUS_HISTORY_LEN) this.focusHistory.shift()
          const meanFocus = this.focusHistory.reduce((a,b)=>a+b,0) / this.focusHistory.length

          if (status === 'good' && meanFocus > this.FOCUS_THRESHOLD) {
            this.focusStableFrames++
            this.stableFrames++
            if (this.stableFrames > this.STABLE_REQUIRED && this.focusStableFrames >= this.FOCUS_STABLE_REQUIRED) {
              this.processing = false
              this.box.visible = false
              this.capturePhoto(bestRect, this.bestPoly)
            }
          } else {
            if (status !== 'good') this.stableFrames = 0
            if (meanFocus <= this.FOCUS_THRESHOLD) this.focusStableFrames = 0
          }
        } else {
          this.box.visible = false
          this.stableFrames = 0
          this.focusStableFrames = 0
          this.bestPoly = null
        }
      } catch (err) {
        console.error('OpenCV processing error:', err)
      } finally {
        if (src) src.delete()
        if (gray) gray.delete()
        if (blurred) blurred.delete()
        if (edges) edges.delete()
        if (contours) contours.delete()
        if (hierarchy) hierarchy.delete()
      }

      if (this.processing) this.rafId = requestAnimationFrame(this.processFrame)
    }
  }
}
</script>

<style scoped>
/* Estética gris minimal y consistente */
.camera-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 10px;
}

.video-wrapper {
  position: relative;
  background: #f3f3f3;
  border: 1px solid #dedede;
  border-radius: 8px;
  overflow: hidden;
  max-width: 480px;
  margin: 0 auto;
}

.video {
  width: 100%;
  display: block;
  background: #e9e9e9;
}

.guide-box {
  position: absolute;
  border: 2px dashed #a8adb3;
  border-radius: 10px;
  opacity: 0.8;
  pointer-events: none;
}

.detected-box {
  position: absolute;
  border-radius: 8px;
  pointer-events: none;
  transition: all 0.15s ease;
  box-sizing: border-box;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.06);
}

.detected-box.bad    { border: 2px solid #c75a5a; }
.detected-box.medium { border: 2px solid #b9b14a; }
.detected-box.good   { border: 2px solid #5aa86a; }

.hidden { display: none; }

.btn-group {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.btn {
  background: #ececec;
  color: #333;
  border: 1px solid #d2d2d2;
  border-radius: 6px;
  padding: 8px 12px;
  min-width: 140px;
  font-weight: 600;
}

.btn:hover:not([disabled]) { background: #e4e4e4; }
button[disabled] { opacity: 0.55; cursor: not-allowed; }

.ocr-block {
  border-top: 1px solid #eee;
  padding-top: 8px;
  display: flex;
  justify-content: center;
}

.ocr-row {
  display: flex;
  max-width: 900px;
  width: 100%;
  justify-content: center;
}

.ocr-image-wrapper {
  background: #fafafa;
  border: 1px solid #e6e6e6;
  padding: 8px;
  border-radius: 6px;
}

.result {
  max-width: 100%;
  display: block;
  border-radius: 8px;
}
</style>



// WEBPACK FOOTER //
// src/views/Camara/scannerID.vue